/* REASON RetailOS™ — App-wide automatic i18n.
 * When the user picks a non-English language, this walks the rendered DOM, batch-translates
 * every visible UI string via /api/i18n/translate (LLM-backed, cached server+client side) and
 * applies it live. A MutationObserver keeps dynamic / route-changed content translated.
 * Switching back to English restores the originals. Strings already covered by the keyed
 * dictionary still work; this is the "fallback so nothing is left untranslated". */
import { useEffect, useRef } from "react";
import { useUx } from "@/lib/UxProvider";
import { api } from "@/lib/api";
import { toast } from "sonner";

const SKIP_TAGS = new Set(["SCRIPT", "STYLE", "NOSCRIPT", "CODE", "PRE", "TEXTAREA", "SVG", "PATH"]);
const HAS_LETTER = /[A-Za-z\u00C0-\u024F]/;
const BATCH = 50;

function shouldSkip(el) {
  while (el) {
    if (el.nodeType === 1) {
      const tag = el.tagName;
      if (SKIP_TAGS.has(tag)) return true;
      if (el.getAttribute && el.getAttribute("data-no-translate") != null) return true;
      if (el.isContentEditable) return true;
    }
    el = el.parentElement;
  }
  return false;
}

function isTranslatable(text) {
  const t = (text || "").trim();
  if (t.length < 2) return false;
  if (!HAS_LETTER.test(t)) return false;     // skip pure numbers / symbols / codes
  if (/^[0-9.,:%₹$€\s/+\-]+$/.test(t)) return false;
  return true;
}

function reApplyWhitespace(original, translated) {
  const lead = original.match(/^\s*/)[0];
  const trail = original.match(/\s*$/)[0];
  return lead + translated.trim() + trail;
}

// True while the user is typing/editing a field, so we can pause DOM re-walking
// (the MutationObserver otherwise re-mutates the DOM mid-keystroke and drops chars).
function isEditing() {
  const el = document.activeElement;
  if (!el) return false;
  const tag = el.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  if (el.isContentEditable) return true;
  return false;
}

export function AutoTranslate() {
  const { prefs } = useUx();
  const lang = prefs.language;
  const origText = useRef(new WeakMap());   // textNode -> original string
  const origAttr = useRef(new WeakMap());   // element -> {placeholder, title}
  const cache = useRef({});                  // src -> translated (current lang)
  const observer = useRef(null);
  const timer = useRef(null);
  const running = useRef(false);

  useEffect(() => {
    // load client cache for this language
    cache.current = {};
    if (lang !== "en") {
      try { cache.current = JSON.parse(localStorage.getItem(`reason_i18n_${lang}`) || "{}"); } catch {}
    }

    const persist = () => {
      try { localStorage.setItem(`reason_i18n_${lang}`, JSON.stringify(cache.current)); } catch {}
    };

    const collect = () => {
      const nodes = [];
      const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT);
      let n;
      while ((n = walker.nextNode())) {
        if (!n.nodeValue || !n.nodeValue.trim()) continue;
        if (shouldSkip(n.parentElement)) continue;
        nodes.push(n);
      }
      // input placeholders
      const els = Array.from(document.querySelectorAll("input[placeholder],textarea[placeholder]"))
        .filter((e) => !shouldSkip(e));
      return { nodes, els };
    };

    const restoreEnglish = () => {
      const { nodes, els } = collect();
      nodes.forEach((node) => {
        const o = origText.current.get(node);
        if (o != null && node.nodeValue !== o) node.nodeValue = o;
      });
      els.forEach((el) => {
        const o = origAttr.current.get(el);
        if (o?.placeholder != null && el.placeholder !== o.placeholder) el.placeholder = o.placeholder;
      });
    };

    const apply = (nodes, els) => {
      observer.current?.disconnect();
      nodes.forEach((node) => {
        const known = origText.current.get(node);
        const cur = node.nodeValue;
        let original = known;
        if (known == null) { original = cur; origText.current.set(node, cur); }
        else {
          const knownT = cache.current[known.trim()];
          if (cur !== known && cur !== (knownT ? reApplyWhitespace(known, knownT) : known)) {
            original = cur; origText.current.set(node, cur);   // React changed text -> new original
          }
        }
        const tr = cache.current[original.trim()];
        const desired = tr ? reApplyWhitespace(original, tr) : original;
        if (node.nodeValue !== desired) node.nodeValue = desired;
      });
      els.forEach((el) => {
        let o = origAttr.current.get(el);
        if (!o) { o = { placeholder: el.placeholder }; origAttr.current.set(el, o); }
        const tr = cache.current[(o.placeholder || "").trim()];
        if (tr && el.placeholder !== tr) el.placeholder = tr;
      });
      connectObserver();
    };

    const run = async () => {
      if (lang === "en" || running.current) return;
      running.current = true;
      try {
        const { nodes, els } = collect();
        // seed originals + gather missing strings
        const need = new Set();
        nodes.forEach((node) => {
          let o = origText.current.get(node);
          if (o == null) { o = node.nodeValue; origText.current.set(node, o); }
          const s = o.trim();
          if (isTranslatable(s) && cache.current[s] == null) need.add(s);
        });
        els.forEach((el) => {
          let o = origAttr.current.get(el);
          if (!o) { o = { placeholder: el.placeholder }; origAttr.current.set(el, o); }
          const s = (o.placeholder || "").trim();
          if (isTranslatable(s) && cache.current[s] == null) need.add(s);
        });
        apply(nodes, els);   // apply whatever we already have immediately

        const missing = Array.from(need);
        for (let i = 0; i < missing.length; i += BATCH) {
          const chunk = missing.slice(i, i + BATCH);
          try {
            const { data } = await api.post("/i18n/translate", { target: lang, texts: chunk });
            Object.assign(cache.current, data.translations || {});
          } catch { /* ignore */ }
        }
        if (missing.length) { persist(); const c = collect(); apply(c.nodes, c.els); }
      } finally {
        running.current = false;
      }
    };

    const schedule = () => {
      if (isEditing()) return; // never re-walk/mutate the DOM while the user is typing in a field
      clearTimeout(timer.current);
      timer.current = setTimeout(() => { lang === "en" ? restoreEnglish() : run(); }, 450);
    };

    const connectObserver = () => {
      if (lang === "en") return;
      if (!observer.current) {
        observer.current = new MutationObserver(() => schedule());
      }
      observer.current.observe(document.body, { childList: true, subtree: true, characterData: true });
    };

    // kick off
    if (lang === "en") {
      observer.current?.disconnect();
      restoreEnglish();
    } else {
      // Reset the DOM back to its stored English originals BEFORE translating to the
      // newly selected language. Without this, switching directly between two non-English
      // languages (e.g. Hindi → Spanish) makes apply() mistake the previous language's text
      // for a React change and corrupt the stored original, leaving the UI stuck.
      restoreEnglish();
      const tid = toast.loading("Translating interface…");
      run().finally(() => toast.dismiss(tid));
    }

    return () => {
      clearTimeout(timer.current);
      observer.current?.disconnect();
    };
  }, [lang]);

  return null;
}

export default AutoTranslate;
